import { useEffect, useState } from 'react';

interface UseAutoUpdateCheckOptions {
  checkOnMount?: boolean;
  checkInterval?: number; // in milliseconds
  onUpdateAvailable?: () => void;
}

export const useAutoUpdateCheck = (options: UseAutoUpdateCheckOptions = {}) => {
  const {
    checkOnMount = true,
    checkInterval = 30 * 60 * 1000, // 30 minutes
    onUpdateAvailable
  } = options;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [lastPromptDismissed, setLastPromptDismissed] = useState<Date | null>(null);

  const checkForUpdates = async (silent = false) => {
    if (!silent) setChecking(true);
    
    try {
      // Check if it's a PWA
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone || 
                    document.referrer.includes('android-app://');
      
      if (isPWA && 'serviceWorker' in navigator) {
        // For PWA, check for service worker updates
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
          
          // Check if there's a waiting service worker (new version)
          if (registration.waiting) {
            // Don't show prompt if user dismissed it recently (within 2 hours)
            const now = new Date();
            if (lastPromptDismissed && (now.getTime() - lastPromptDismissed.getTime()) < 2 * 60 * 60 * 1000) {
              setLastChecked(new Date());
              return false;
            }
            
            setUpdateAvailable(true);
            if (onUpdateAvailable) {
              onUpdateAvailable();
            }
            return true;
          }
        }
      }
      
      setLastChecked(new Date());
      return false;
    } catch (error) {
      console.error('Error checking for updates:', error);
      return false;
    } finally {
      if (!silent) setChecking(false);
    }
  };

  const applyUpdate = async () => {
    try {
      // Check if it's a PWA
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone || 
                    document.referrer.includes('android-app://');
      
      if (isPWA) {
        // For PWA, try service worker update first, then force refresh
        try {
          if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration && registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
          }
        } catch (swError) {
          console.warn('Service worker update failed:', swError);
        }
        
        // Clear caches and force refresh
        if ('caches' in window) {
          try {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
          } catch (cacheError) {
            console.warn('Cache clearing failed:', cacheError);
          }
        }
        
        // Force reload with timestamp to bypass all caching
        const url = new URL(window.location.href);
        url.searchParams.set('_auto_update', Date.now().toString());
        window.location.href = url.toString();
      } else {
        // For regular web app, force refresh
        const url = new URL(window.location.href);
        url.searchParams.set('_auto_update', Date.now().toString());
        window.location.href = url.toString();
      }
    } catch (error) {
      console.error('Error applying update:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  };

  const dismissUpdate = () => {
    setUpdateAvailable(false);
    setLastPromptDismissed(new Date());
  };

  // Check for updates on mount
  useEffect(() => {
    if (checkOnMount) {
      // Delay initial check to avoid blocking app startup
      const timer = setTimeout(() => {
        checkForUpdates(true); // Silent check on startup
      }, 3000); // 3 second delay

      return () => clearTimeout(timer);
    }
  }, [checkOnMount]);

  // Set up periodic checking
  useEffect(() => {
    if (checkInterval > 0) {
      const interval = setInterval(() => {
        checkForUpdates(true); // Silent periodic checks
      }, checkInterval);

      return () => clearInterval(interval);
    }
  }, [checkInterval]);

  // Listen for service worker updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleServiceWorkerUpdate = () => {
        setUpdateAvailable(true);
        if (onUpdateAvailable) {
          onUpdateAvailable();
        }
      };

      navigator.serviceWorker.addEventListener('controllerchange', handleServiceWorkerUpdate);
      
      return () => {
        navigator.serviceWorker.removeEventListener('controllerchange', handleServiceWorkerUpdate);
      };
    }
  }, [onUpdateAvailable]);

  return {
    updateAvailable,
    checking,
    lastChecked,
    lastPromptDismissed,
    checkForUpdates,
    applyUpdate,
    dismissUpdate
  };
};
